import { observable, computed, action, reaction } from 'mobx';
import { app } from './../App';
import { Lead, PricingRequest, User } from '.';
import * as moment from 'moment';
import {
  IPricingItemSearchApi,
  IPricingFullApi,
  IPricingRequestFindApi,
  IPricingNewRequest,
  StatusOfNewPricingRequest,
  IPriceItem,
  IPricingRequestDetails,
  IGroupPricingSearchApi,
  IGroupPriceItem,
} from 'common/interfaces/pricing';
import { formatNumber, unformat } from 'accounting';

// Store
export class LeadPricing {
  constructor(
    private lead: Lead,
    private pricingRequest: PricingRequest,
    private user: User
  ) {}

  private reactToOpenLeadIdChange = reaction(
    () => this.lead.openLeadId,
    (leadId: number) => {
      if (leadId !== null) {
        this.fetchAllItemsWithPricing();
        this.fetchPendingPricingRequests();
      }
    }
  );

  private reactToOpenLeadGroupChange = reaction(
    () => this.lead.openLeadGroup,
    (group) => {
      this.fetchGroupPricing();
    }
  );

  @observable public pricingItemListFetched: boolean = false;
  @observable public pricingItemListLoading: boolean = false;

  @observable
  public statusOfUpdatingPricesForAllItems: StatusOfNewPricingRequest =
    StatusOfNewPricingRequest.None;

  @observable public allPricingItemListLoading: boolean = false;
  @observable public allPricingItemList: IPriceItem[] = [];

  @computed public get allPricingItemListLength(): number {
    return this.allPricingItemList.length;
  }

  @computed public get allPricingItemListIds(): number[] {
    return this.allPricingItemList.map((item) => item.id);
  }

  @observable public groupPricingItemListLoading: boolean = false;
  @observable public groupPricingItemList: IGroupPriceItem[] = [];

  @computed public get groupPricingItemListLength(): number {
    return this.groupPricingItemList.length;
  }

  @computed public get groupPricingItemListItemCodes(): string[] {
    return this.groupPricingItemList.map((group) => group.itemcode);
  }

  @observable public modifiedAnyItemPricing: boolean = false;
  @observable public modifiedAnyGroupPricing: boolean = false;

  @observable public pricingSearch: string = '';

  @computed public get pricingItemSearchResults(): IPriceItem[] {
    if (this.pricingSearch) {
      const regex = new RegExp(/-|%|,|\.|\/|\s/gi);
      const query: string = this.pricingSearch.toLowerCase().replace(regex, '');
      return this.allPricingItemList.filter((item, i) => {
        if (this.newRequestItemsIds.indexOf(item.id) < 0) {
          const itemcode = item.itemcode.toLowerCase().replace(regex, '');
          const itemname = item.itemname.toLowerCase().replace(regex, '');
          return (
            itemcode.indexOf(query) !== -1 || itemname.indexOf(query) !== -1
          );
        } else {
          return false;
        }
      });
    } else {
      return this.allPricingItemList.filter((item, i) => {
        return this.newRequestItemsIds.indexOf(item.id) < 0;
      });
    }
  }

  @computed public get pricingItemSearchResultsLength(): number {
    return this.pricingItemSearchResults.length;
  }

  @observable public leadItemsPricingModal: boolean = false;

  @computed
  public get leadItemsPricingModalData(): IPricingRequestDetails | null {
    const now = moment().format();

    return this.lead.openLeadId !== null && this.lead.openLeadDetails !== null
      ? {
          id: 0,
          cardcode: this.lead.openLeadDetails.cardcode,
          item: this.allPricingItemList.map((item) => ({
            pricingRequestId: 0,
            id: item.id,
            itemcode: item.itemcode,
            itemData: {
              itemname: item.itemname,
              price: formatNumber(item.price, 2),
            },
            volume: formatNumber(item.volume, 0),
            approved: item.approved ? true : false,
            approvedByUserId: item.approvedByUserId,
            approvedPrice:
              typeof item.customerPrice === 'number'
                ? formatNumber(item.customerPrice, 2)
                : item.customerPrice,
            expectedPrice: formatNumber(item.expectedPrice, 2),
          })),
          groupItems: this.groupPricingItemList,
          providerGroup:
            this.lead.openLeadDetails.providerGroup !== null &&
            typeof this.lead.openLeadDetails.providerGroup.id !== 'undefined' &&
            this.lead.openLeadDetails.providerGroup.id !== null &&
            typeof this.lead.openLeadDetails.providerGroup.name !==
              'undefined' &&
            this.lead.openLeadDetails.providerGroup.name !== null
              ? {
                  id: this.lead.openLeadDetails.providerGroup.id,
                  name: this.lead.openLeadDetails.providerGroup.name,
                }
              : null,
          note: null,
          complete: true,
          createdAt: now,
          updatedAt: now,
          createdByUserId: this.user.id,
          createdByUser: {
            id: this.user.id,
            name: this.user.name,
            username: this.user.username,
            email: this.user.email,
          },
          providerId: this.lead.openLeadId,
          provider: {
            id: this.lead.openLeadId,
            providerId: this.lead.openLeadDetails.providerId,
            dba: this.lead.openLeadDetails.dba,
            legalBusName: this.lead.openLeadDetails.legalBusName,
          },
          approvedPricesModified: this.modifiedAnyItemPricing,
          groupPricesModified: this.modifiedAnyGroupPricing,
        }
      : null;
  }

  @action public changeApprovedPriceOfLeadsItem = (
    itemId: number,
    price: string
  ) => {
    const itemIndex = this.allPricingItemListIds.indexOf(itemId);
    if (itemIndex !== -1) {
      this.allPricingItemList[itemIndex].customerPrice = price;

      this.modifiedAnyItemPricing = true;
    }
  };

  @action public changeGroupPriceOfLeadsItem = (
    itemId: number,
    price: string
  ) => {
    const itemIndex = this.allPricingItemListIds.indexOf(itemId);
    if (itemIndex !== -1) {
      const itemCode: string = this.allPricingItemList[itemIndex].itemcode;
      const itemCodeIndex = this.groupPricingItemListItemCodes.indexOf(
        itemCode
      );
      if (itemCodeIndex !== -1) {
        this.groupPricingItemList[itemCodeIndex].price = price;
      } else {
        this.groupPricingItemList.push({
          itemcode: itemCode,
          price,
        });
      }

      this.modifiedAnyGroupPricing = true;
    }
  };

  @observable public newRequest: IPricingNewRequest = {
    showModal: false,
    status: StatusOfNewPricingRequest.None,
    providerId: null,
    items: [],
    isNew: true,
    id: null,
    descriptionHtml: '',
    descriptionText: '',
  };

  @computed public get newRequestItemsIds(): number[] {
    return this.newRequest.items.map((item) => item.id);
  }

  @computed public get newRequestItemsLength(): number {
    return this.newRequestItemsIds.length;
  }

  @observable public itemId: number = null;

  @computed public get pricingSearchLength(): number {
    return this.pricingSearch.length;
  }

  @computed public get pricingRequestLength(): number {
    return this.pricingSearch.length;
  }

  @action public showCreatePricingRequestModal = (requestIsNew?: boolean) => {
    this.newRequest.showModal = true;
    this.pricingSearch = '';

    if (typeof requestIsNew !== 'undefined') {
      this.newRequest.isNew = requestIsNew;
    }
  };

  @action public hideCreatePricingRequestModal = () => {
    this.newRequest.showModal = false;
    this.newRequest.status = StatusOfNewPricingRequest.None;
    this.newRequest.providerId = null;
    this.newRequest.items = [];
    this.newRequest.isNew = true;
    this.newRequest.id = null;
    this.newRequest.descriptionHtml = '';
    this.newRequest.descriptionText = '';
  };

  @action public addItemToPriceRequest = (item: IPriceItem) => {
    const itemInPriceRequest = this.newRequestItemsIds.indexOf(item.id);
    if (itemInPriceRequest < 0) {
      this.newRequest.items.push({
        id: item.id,
        isNew: true,
        itemcode: item.itemcode,
        itemname: item.itemname || '',
        price: item.price,
        approvedPrice:
          typeof item.approvedPrice !== 'undefined' &&
          item.approvedPrice !== null
            ? formatNumber(item.approvedPrice, 2)
            : null,
        expectedPrice: null,
        volume: null,
      });
    }
  };

  @action public removeItemFromPriceRequest = (itemId: number) => {
    const itemInPriceRequest = this.newRequestItemsIds.indexOf(itemId);
    if (itemInPriceRequest !== -1) {
      this.newRequest.items.splice(itemInPriceRequest, 1);
    }
  };

  @action public priceRequestItemChangePrice = (
    itemId: number,
    price: string
  ) => {
    const itemIndex = this.newRequestItemsIds.indexOf(itemId);
    if (itemIndex !== -1) {
      this.newRequest.items[itemIndex].expectedPrice = price;
    }
  };

  @action public priceRequestItemChangeVolume = (
    itemId: number,
    volume: string
  ) => {
    const itemIndex = this.newRequestItemsIds.indexOf(itemId);
    if (itemIndex !== -1) {
      this.newRequest.items[itemIndex].volume = volume;
    }
  };

  @action public priceRequestItemChangeAprovedPrice = (
    itemId: number,
    approvedPrice: string
  ) => {
    const itemIndex = this.newRequestItemsIds.indexOf(itemId);
    if (itemIndex !== -1) {
      this.newRequest.items[itemIndex].approvedPrice = approvedPrice;
    }
  };

  @action public fetchAllItemsWithPricing = async (): Promise<IPriceItem[]> => {
    try {
      if (this.lead.openLeadId !== null) {
        this.allPricingItemListLoading = true;

        let query: {
          $limit: number;
          $skip: number;
          providerId?: number;
          cardcode?: string;
        } = {
          $limit: 999999,
          $skip: 0,
        };

        if (this.lead.openLeadId !== null) {
          query = {
            ...query,
            providerId: this.lead.openLeadId,
          };
        }

        if (
          this.lead.openLeadDetails !== null &&
          typeof this.lead.openLeadDetails.cardcode !== 'undefined' &&
          this.lead.openLeadDetails.cardcode !== null
        ) {
          query = {
            ...query,
            cardcode: this.lead.openLeadDetails.cardcode,
          };
        }

        const itemsPricingService = await app.service('item');
        const itemsPricing: IPricingItemSearchApi = await itemsPricingService.find(
          { query }
        );

        this.allPricingItemList = itemsPricing.data;

        return itemsPricing.data;
      } else {
        return this.allPricingItemList;
      }
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.allPricingItemListLoading = false;
    }
  };

  // Fetch group pricing
  @action public fetchGroupPricing = async (): Promise<boolean> => {
    try {
      if (this.lead.openLeadGroup !== null) {
        const groupPricingService = await app.service('group-price');
        const groupPricing: IGroupPricingSearchApi = await groupPricingService.find(
          {
            query: {
              groupId: this.lead.openLeadDetails.providerGroup.id,
              $skip: 0,
              $limit: 999999,
            },
          }
        );

        this.groupPricingItemList = groupPricing.data;
      } else {
        this.groupPricingItemList = [];
      }

      return true;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.groupPricingItemListLoading = false;
    }
  };

  // Create pricing request
  @action public createPricingRequest = async (): Promise<IPriceItem[]> => {
    try {
      this.newRequest.status = StatusOfNewPricingRequest.Saving;

      // Check if all items have required information
      let itemErrorsCount: number = 0;

      // Map items to API supported data structure
      const items = this.newRequest.items.map((item) => {
        if (
          typeof item.expectedPrice === 'undefined' ||
          item.expectedPrice === null ||
          typeof item.volume === 'undefined' ||
          item.volume === null
        ) {
          itemErrorsCount++;
        }

        return {
          itemcode: item.itemcode,
          volume: unformat(item.volume),
          expectedPrice: unformat(item.expectedPrice),
          approvedPrice: unformat(item.approvedPrice),
        };
      });

      if (itemErrorsCount > 0) {
        this.newRequest.status =
          StatusOfNewPricingRequest.ErrorNotAllItemsHaveQuantityAndPrice;
        throw new Error(
          'Not all items have all required information provided by the user'
        );
      }

      // Get all items visible to the user
      const pricingService = await app.service('pricing-request');
      const pricingItem = await pricingService.create({
        item: items,
        note: this.newRequest.descriptionText,
        providerId: this.lead.openLeadId,
      });

      await this.fetchPendingPricingRequests();

      this.newRequest.status = StatusOfNewPricingRequest.Success;
      setTimeout(() => {
        this.hideCreatePricingRequestModal();
      }, 512);

      this.pricingRequest.fetchAllOpenPricingRequests();

      return pricingItem;
    } catch (err) {
      if (this.newRequest.status === StatusOfNewPricingRequest.Saving) {
        this.newRequest.status = StatusOfNewPricingRequest.ErrorTryAgain;
      }
      console.error(err);
      return err;
    }
  };

  // Create item by provider id
  @action public patchPricingRequest = async (): Promise<IPriceItem[]> => {
    try {
      // Map items to API supported data structure
      const items = this.newRequest.items.map((item) => ({
        id: item.isNew ? null : item.id,
        itemcode: item.itemcode,
        volume: item.volume,
        expectedPrice: item.expectedPrice,
        approvedPrice: item.approvedPrice,
      }));

      // Get all items visible to the user
      const pricingService: any = app.service('pricing-request');
      const pricingItem = await pricingService.patch(this.newRequest.id, {
        item: items,
        note: this.newRequest.descriptionText,
        providerId: this.newRequest.providerId,
      });

      // Return pricing search item data
      return pricingItem;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Aprove price item
  @action public removePriceItem = (index: number) => {
    try {
      // Remove single id
      let removeItem;
      if (this.newRequest.items[index].id) {
        const pricingService: any = app.service(`pricing-request-line`);
        removeItem = pricingService.remove(this.newRequest.items[index].id);
      }
      this.newRequest.items.splice(index, 1);

      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Fetch item by provider id
  @action public fetchPriceRequestByProviderId = async (): Promise<
    IPriceItem[]
  > => {
    try {
      const pricingService: any = app.service('pricing-request');
      const pricingItem: IPricingRequestFindApi = await pricingService.find({
        query: {
          providerId: this.lead.openLeadId,
          $limit: 1,
          $skip: 0,
        },
      });

      let response: Partial<IPricingFullApi> = { item: [] };
      if (pricingItem.data.length > 0) {
        this.newRequest.id = pricingItem.data[0].id;
        this.newRequest.descriptionText = pricingItem.data[0].note;
        this.newRequest.descriptionHtml = pricingItem.data[0].note;
        this.newRequest.isNew = false;

        response = await pricingService.get(pricingItem.data[0].id);
      }
      // this.newRequest.items = response.item;

      return response.item;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Fetch item by provider id
  @action public clearAll = () => {
    this.newRequest = {
      showModal: false,
      status: StatusOfNewPricingRequest.None,
      providerId: null,
      isNew: true,
      items: [],
      id: null,
      descriptionHtml: '',
      descriptionText: '',
    };
  };

  // Pending pricing requests
  @observable public loadingPendingPricingRequests: boolean = false;
  @observable public fetchedPendingPricingRequests: boolean = false;
  @observable public pendingPricingRequests: IPricingRequestDetails[] = [];

  // Fetch pending pricing requests
  @action public fetchPendingPricingRequests = async (): Promise<
    IPricingRequestDetails[]
  > => {
    try {
      this.loadingPendingPricingRequests = true;

      const pricingRequestService = await app.service('pricing-request');
      const pendingPricingRequests: {
        total: number;
        skip: number;
        limit: number;
        data: IPricingRequestDetails[];
      } = await pricingRequestService.find({
        query: {
          providerId: this.lead.openLeadId,
          complete: false,
          $skip: 0,
          $limit: 99999,
          $sort: {
            updatedAt: -1,
          },
        },
      });

      this.fetchedPendingPricingRequests = true;
      this.pendingPricingRequests = pendingPricingRequests.data;

      return pendingPricingRequests.data;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.loadingPendingPricingRequests = false;
    }
  };

  @action public saveUpdatedProviderAndGroupPrices = async (): Promise<
    boolean
  > => {
    try {
      this.statusOfUpdatingPricesForAllItems = StatusOfNewPricingRequest.Saving;

      if (this.leadItemsPricingModalData !== null) {
        const pricingUpdateService = await app.service('pricing-update');

        // Check if group prices have been modified and update them
        if (
          this.leadItemsPricingModalData.groupPricesModified &&
          this.leadItemsPricingModalData.providerGroup !== null
        ) {
          const groupItemsPricing = this.allPricingItemList.map((item) => {
            const itemGroupIndex = this.groupPricingItemListItemCodes.indexOf(
              item.itemcode
            );
            const itemGroupPrice: number | null =
              itemGroupIndex !== -1
                ? this.groupPricingItemList[itemGroupIndex].price.trim() !== ''
                  ? unformat(this.groupPricingItemList[itemGroupIndex].price)
                  : null
                : null;

            return {
              id: item.id,
              itemcode: item.itemcode,
              price: itemGroupPrice !== null ? itemGroupPrice : 0,
              delete: itemGroupPrice === null,
            };
          });

          await pricingUpdateService.create({
            groupId: this.leadItemsPricingModalData.providerGroup.id,
            item: groupItemsPricing,
          });
        }

        // Check if approved pricing items have been modified
        if (this.leadItemsPricingModalData.approvedPricesModified) {
          let allItems = this.allPricingItemList.map((item) => {
            const itemPrice: number | null =
              item.customerPrice !== null
                ? typeof item.customerPrice === 'string' &&
                  item.customerPrice.trim() !== ''
                  ? unformat(item.customerPrice)
                  : null
                : typeof item.customerPrice === 'number'
                ? item.customerPrice
                : null;

            return {
              id: item.id,
              itemcode: item.itemcode,
              volume: 1,
              expectedPrice: itemPrice !== null ? itemPrice : null,
              approvedPrice: itemPrice !== null ? itemPrice : null,
              price: itemPrice !== null ? itemPrice : null,
              delete: itemPrice === null,
            };
          });

          // Check if provider is customer and has card code, if not - create instantly approved provider pricing request
          if (this.leadItemsPricingModalData.cardcode) {
            await pricingUpdateService.create({
              cardcode: this.leadItemsPricingModalData.cardcode,
              item: allItems,
            });
          } else {
            allItems = allItems.filter((item) => item.delete === false);

            const pricingRequestService = await app.service('pricing-request');
            await pricingRequestService.create({
              item: allItems,
              note: null,
              providerId: this.lead.openLeadId,
              complete: true,
            });
          }
        }
      }

      this.statusOfUpdatingPricesForAllItems =
        StatusOfNewPricingRequest.Success;

      this.fetchAllItemsWithPricing();
      this.fetchGroupPricing();

      setTimeout(() => {
        this.leadItemsPricingModal = false;
        this.statusOfUpdatingPricesForAllItems = StatusOfNewPricingRequest.None;
      }, 1024);

      return true;
    } catch (err) {
      this.statusOfUpdatingPricesForAllItems =
        StatusOfNewPricingRequest.ErrorTryAgain;
      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/LeadPricing.ts